'use client';

import { ErrorInfo, FC, useCallback, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { ErrorBoundary as ReactErrorBoundary, ErrorBoundaryProps, FallbackProps } from 'react-error-boundary';
import * as Sentry from '@sentry/react';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Colors } from '../../../constants';
import { Button } from '../../buttons';

export const ErrorBoundary: FC<Omit<ErrorBoundaryProps, 'fallback' | 'fallbackRender'>> = ({
	FallbackComponent,
	children,
}) => {
	const logError = useCallback((error: Error, errorInfo: ErrorInfo) => {
		// Do something with the error, e.g. log to an external API
		// f.e. Sentry.captureException(error, { extra: errorInfo });

		console.info('[RootErrorBoundary]');
		console.error(error);

		if (process.env['NODE_ENV'] === 'production') {
			Sentry.withScope(scope => {
				scope.setExtras({ ...errorInfo });
				const eventId = Sentry.captureException(
					error instanceof Error ? error : new Error(error?.['message'] || error?.['description'] || error?.['name'] || error?.['error'] || JSON.stringify(error)),
					{
						level: 'fatal',
						contexts: {
							react: {
								componentStack: errorInfo.componentStack
							}
						},
						extra: {
							errorInfo
						}
					});
				console.info(`Reported to Sentry with eventId: ${eventId}`);
			});
		}
	}, []);

	return (
		<ReactErrorBoundary FallbackComponent={FallbackComponent ?? ErrorBoundaryFallback} onError={logError}>
			{children}
		</ReactErrorBoundary>
	);
};

export function ErrorBoundaryFallback (props: FallbackProps) {
	const { error, resetErrorBoundary } = props;

	const isDevelopment = process.env['NODE_ENV'] === 'development';

	const [showDetails, setShowDetails] = useState(false);

	const handleResetError = useCallback(() => {
		if (!resetErrorBoundary || error?.name === 'ChunkLoadError') {
			window.location.reload();
		} else {
			resetErrorBoundary();
		}
	}, [error, resetErrorBoundary]);

	return (
		<Box
			role='alert'
			sx={{
				position: 'fixed',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				backgroundColor: Colors.black_a90,
				display: 'flex',
				flexDirection: 'column',
				gap: '1rem',
				p: '2rem',
			}}
		>
			<Typography
				variant='h4'
				sx={{
					fontFamily: 'Montserrat, sans-serif',
					color: Colors.white,
				}}
			>
				Oops! Something went wrong:
			</Typography>

			<Typography
				sx={{
					fontFamily: 'Montserrat, sans-serif',
					color: Colors.white,
				}}
			>
				{isDevelopment
					? 'Dev mode detected. Please fix this error before deploying to production.'
					: 'This error has been logged and we will look into it'}
			</Typography>

			<Typography
				variant='body1'
				component='pre'
				sx={{
					color: Colors.error,
					backgroundColor: Colors.white_a5,
					p: '16px',
					borderRadius: '8px',
					position: 'relative',
					textWrap: 'wrap',
				}}
			>
				<IconButton
					onClick={() => setShowDetails(s => !s)}
					sx={{
						position: 'absolute',
						top: '8px',
						right: '10px',
						color: Colors.white,
						fontFamily: 'Montserrat, sans-serif',
						textTransform: 'none',
						fontWeight: 600,
					}}
				>
					{showDetails ? <ExpandLess /> : <ExpandMore />}
				</IconButton>

				{showDetails ? error.stack : `${error.name}: ${error.message}`}
			</Typography>

			<Box>
				<Button
					onClick={handleResetError}
					strictColor
					sx={{
						fontFamily: 'Montserrat, sans-serif',
						textTransform: 'none',
						fontWeight: 600,
					}}
				>
					Close and Try again
				</Button>
			</Box>
		</Box>
	);
}
